import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import createCustomer from '@/shared/api/customers/createCustomer';
import updateCustomer from '@/shared/api/customers/updateCustomer';
import useModalDialog from '@/shared/hooks/useModalDialog';
import useNotificationBar from '@/shared/hooks/useNotificationBar';
import useProgressDialog from '@/shared/hooks/useProgressDialog';
import useRedirectLogin from '@/shared/hooks/useRedirectLogin';
import Customer from '@/shared/types/customer.type';
import DialogName from '@/shared/types/dialogName.enum';
import OrderCustomer from '@/shared/types/order/orderCustomer.type';

import transformCustomer from '../utils/transformCustomer';

type OnUpdateDialogState = ReturnType<
  typeof useModalDialog<unknown, DialogName>
>['onUpdateDialogState'];

type OnUpdateNotificationBar = ReturnType<
  typeof useNotificationBar
>['onUpdateNotificationBar'];

type OnUpdateProgressDialog = ReturnType<
  typeof useProgressDialog
>['onUpdateProgressDialog'];

type Args = {
  onUpdateDialogState: OnUpdateDialogState;
  onUpdateNotificationBar: OnUpdateNotificationBar;
  onUpdateProgressDialog: OnUpdateProgressDialog;
};

const useCustomerOperations = ({
  onUpdateDialogState,
  onUpdateNotificationBar,
  onUpdateProgressDialog,
}: Args) => {
  const { t } = useTranslation(['customers', 'common']);
  const redirectLogin = useRedirectLogin();

  const [createdCustomer, setCreatedCustomer] = useState<
    OrderCustomer[] | null
  >(null);

  const [updatedCustomer, setUpdatedCustomer] = useState<
    OrderCustomer[] | null
  >(null);

  const resetCustomerStates = () => {
    setCreatedCustomer(null);
    setUpdatedCustomer(null);
  };

  const handleCreateCustomer = async (customer: Partial<Customer>) => {
    onUpdateProgressDialog({ open: true });
    resetCustomerStates();

    const response = await createCustomer(customer, redirectLogin);

    if (response.success) {
      onUpdateNotificationBar({
        message: t('customers:customerCreated'),
        severity: 'success',
        open: true,
      });
      onUpdateProgressDialog({ open: false });
      onUpdateDialogState(DialogName.CreateCustomer, { open: false });
      setCreatedCustomer(transformCustomer(response.data[0]));
    } else {
      onUpdateProgressDialog({ open: false });
      onUpdateDialogState(DialogName.CreateCustomer, {
        errors: [response.msg],
      });
    }
  };

  const handleUpdateCustomer = async (
    customerId: string,
    customer: Partial<Customer>,
  ) => {
    onUpdateProgressDialog({ open: true });
    resetCustomerStates();

    const response = await updateCustomer(customerId, customer, redirectLogin);

    if (response.success) {
      onUpdateNotificationBar({
        message: t('customers:customerUpdated'),
        severity: 'success',
        open: true,
      });
      onUpdateProgressDialog({ open: false });
      onUpdateDialogState(DialogName.EditCustomer, { open: false });
      setUpdatedCustomer(transformCustomer(response.data[0]));
    } else {
      onUpdateProgressDialog({ open: false });
      onUpdateDialogState(DialogName.EditCustomer, {
        errors: [response.msg],
      });
    }
  };

  return {
    createdCustomer,
    updatedCustomer,
    handleCreateCustomer,
    handleUpdateCustomer,
  };
};

export default useCustomerOperations;
